"use client"

import type { KeyboardEvent } from "react"
import type { UseFormRegisterReturn } from "react-hook-form"
import type { GetEventsBusySlots200SlotsItem, ProviderMeetingAttachmentV1 } from "openapi/api/model"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { formatBusySlotTime } from "@/lib/calendar-busy-slots"

type OverlapWarningProps = {
  slots: GetEventsBusySlots200SlotsItem[]
}

export function OverlapWarning({ slots }: OverlapWarningProps) {
  if (slots.length === 0) return null

  return (
    <div className="rounded-md border border-attention/35 bg-attention/[0.08] p-3 text-[12.5px] text-attention">
      <p className="font-medium">
        This time overlaps with {slots.length} busy slot(s).
      </p>
      <div className="mt-1 space-y-1">
        {slots.slice(0, 3).map((slot, index) => (
          <p key={`${slot.start}-${slot.end}-${index}`}>
            {formatBusySlotTime(slot.start)}{" "}
            -{" "}
            {formatBusySlotTime(slot.end)}{" "}
            ({slot.eventName?.trim() || "Busy"})
          </p>
        ))}
        {slots.length > 3 && <p>...and {slots.length - 3} more</p>}
      </div>
    </div>
  )
}

type EventInviteSectionProps = {
  sendInvites: boolean
  onSendInvitesChange: (checked: boolean) => void
  inviteeDraft: string
  onInviteeDraftChange: (value: string) => void
  onInviteeDraftKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void
  onInviteeDraftBlur: () => void
  inviteeEmails: string[]
  onRemoveInviteeEmail: (email: string) => void
  includeInviteMessage?: boolean
  onIncludeInviteMessageChange?: (checked: boolean) => void
  inviteMessageField: UseFormRegisterReturn<"invite_message">
  existingAttachments?: ProviderMeetingAttachmentV1[]
  newAttachments?: File[]
  onAddAttachments: (files: File[]) => void
  onRemoveExistingAttachment: (id: string) => void
  onRemoveNewAttachment: (index: number) => void
  showAttachments?: boolean
  showCandidateEmailHint?: boolean
  attendeeEditMode?: boolean
  supportsInviteMessage?: boolean
}

export function EventInviteSection({
  sendInvites,
  onSendInvitesChange,
  inviteeDraft,
  onInviteeDraftChange,
  onInviteeDraftKeyDown,
  onInviteeDraftBlur,
  inviteeEmails,
  onRemoveInviteeEmail,
  includeInviteMessage = false,
  onIncludeInviteMessageChange,
  inviteMessageField,
  existingAttachments = [],
  newAttachments = [],
  onAddAttachments,
  onRemoveExistingAttachment,
  onRemoveNewAttachment,
  showAttachments = true,
  showCandidateEmailHint = false,
  attendeeEditMode = false,
  supportsInviteMessage = true,
}: EventInviteSectionProps) {
  return (
    <div className="space-y-4 rounded-lg border p-4">
      {!attendeeEditMode && (
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label htmlFor="send_invites" className="text-sm md:text-base">Send calendar invites</Label>
            <p className="text-xs text-muted-foreground">
              Notify invitees when this event is created.
            </p>
          </div>
          <Switch
            id="send_invites"
            checked={!!sendInvites}
            onCheckedChange={onSendInvitesChange}
            className="border-border bg-muted data-[state=unchecked]:border-border data-[state=unchecked]:bg-muted-foreground/20 data-[state=checked]:border-primary"
          />
        </div>
      )}

      {(attendeeEditMode || sendInvites) && (
        <>
          <div className="space-y-2">
            <Label htmlFor="invitee_emails" className="text-sm md:text-base">
              {attendeeEditMode ? "Attendees" : "Invitee Emails"}
            </Label>
            <Input
              id="invitee_emails"
              value={inviteeDraft}
              onChange={(event) => onInviteeDraftChange(event.target.value)}
              onKeyDown={onInviteeDraftKeyDown}
              onBlur={onInviteeDraftBlur}
              placeholder="Type email and press comma"
              className="text-sm md:text-base"
            />
            {(inviteeEmails?.length ?? 0) > 0 && (
              <div className="flex flex-wrap gap-2">
                {inviteeEmails.map((email) => (
                  <Badge key={email} variant="secondary" className="gap-1 pr-1">
                    <span>{email}</span>
                    <button
                      type="button"
                      className="rounded-sm p-0.5 hover:bg-muted"
                      onClick={() => onRemoveInviteeEmail(email)}
                      aria-label={`Remove ${email}`}
                    >
                      x
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            {showCandidateEmailHint && (
              <p className="text-xs text-muted-foreground">
                Candidate email is auto-added when contact info is available.
              </p>
            )}
          </div>

          {!attendeeEditMode && supportsInviteMessage && (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label htmlFor="include_invite_message" className="text-sm md:text-base">Include custom invite message</Label>
                  <p className="text-xs text-muted-foreground">
                    Off by default so no message is sent unless you explicitly enable it.
                  </p>
                </div>
                <Switch
                  id="include_invite_message"
                  checked={includeInviteMessage}
                  onCheckedChange={(checked) => onIncludeInviteMessageChange?.(!!checked)}
                  className="border-border bg-muted data-[state=unchecked]:border-border data-[state=unchecked]:bg-muted-foreground/20 data-[state=checked]:border-primary"
                />
              </div>

              {includeInviteMessage && (
                <div className="space-y-2">
                  <Label htmlFor="invite_message" className="text-sm md:text-base">
                    Invite Message
                  </Label>
                  <Textarea
                    id="invite_message"
                    {...inviteMessageField}
                    placeholder="Add a message for invitees..."
                    rows={3}
                    className="text-sm md:text-base"
                  />
                </div>
              )}
            </div>
          )}

          {!attendeeEditMode && !supportsInviteMessage && (
            <p className="text-xs text-muted-foreground">
              Custom invite messages are not supported for the connected Google calendar provider.
            </p>
          )}

          {showAttachments && (
          <div className="space-y-2">
            <Label htmlFor="attachments" className="text-sm md:text-base">
              {attendeeEditMode ? "Attachments" : "Attach Files"}
            </Label>
            {existingAttachments.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Current provider attachments</p>
                <div className="flex flex-wrap gap-2">
                  {existingAttachments.map((attachment) => (
                    <Badge key={attachment.id ?? attachment.filename} variant="secondary" className="gap-1 pr-1">
                      <span>{attachment.filename ?? "Attachment"}</span>
                      {attachment.id && (
                        <button
                          type="button"
                          className="rounded-sm p-0.5 hover:bg-muted"
                          onClick={() => onRemoveExistingAttachment(attachment.id!)}
                          aria-label={`Remove ${attachment.filename ?? "attachment"}`}
                        >
                          x
                        </button>
                      )}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {newAttachments.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">New files to upload</p>
                <div className="flex flex-wrap gap-2">
                  {newAttachments.map((file, index) => (
                    <Badge key={`${file.name}-${file.size}-${index}`} variant="outline" className="gap-1 pr-1">
                      <span>{file.name}</span>
                      <button
                        type="button"
                        className="rounded-sm p-0.5 hover:bg-muted"
                        onClick={() => onRemoveNewAttachment(index)}
                        aria-label={`Remove ${file.name}`}
                      >
                        x
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            <Input
              id="attachments"
              type="file"
              multiple
              onChange={(event) => {
                const files = Array.from(event.target.files ?? [])
                onAddAttachments(files)
                event.target.value = ""
              }}
              className="text-sm md:text-base"
            />
          </div>
          )}
        </>
      )}
    </div>
  )
}
